import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LwButton, LwModal, LwSearchInput } from '@/components/lw';
import {
  addPaymentLink,
  listUnresolvedPaymentLinks,
  searchPaymentLinkLoans,
  type AddPaymentLinkInput
} from '@/services/appraisalWorkspaceService';
import { PaymentLinkFooter, PaymentLinkForm } from '@/components/encompass/prospects/appraisal/AppraisalPaymentLinks';
import { appraisalDate } from '@/components/encompass/prospects/appraisal/appraisalDate';
import type {
  AppraisalPaymentLinkLoan,
  AppraisalPaymentLinkLoanSearch
} from '../../../supabase/functions/_shared/appraisal-workspace-contract';

const SEARCH_DELAY_MS = 250;

function propertyLine(loan: Pick<AppraisalPaymentLinkLoan, 'propertyStreet' | 'propertyLocality'>): string {
  return [loan.propertyStreet, loan.propertyLocality].filter(Boolean).join(', ') || 'Property address not recorded';
}

/**
 * Payment links → Add payment link: find the loan, then the same form as the loan's Appraisal tab. One dialog with
 * two screens, so focus never crosses between dialogs. Only loans the viewer could save a link to are listed.
 */
export function ReportAddPaymentLinkModal({
  isAdmin,
  onClose,
  onSaved,
  onReload
}: {
  isAdmin: boolean;
  onClose: () => void;
  /** Saved while this dialog is open: it closes, the report reloads and focus moves to the link. */
  onSaved: (requestId: string | null) => void;
  /** Reloads the report: after a failed save the server may still hold the link, and a save can finish after its dialog closed. */
  onReload: () => Promise<void>;
}) {
  const formId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ term: string; result: AppraisalPaymentLinkLoanSearch } | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [loan, setLoan] = useState<AppraisalPaymentLinkLoan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);
  const saving = useRef(false);
  const live = useRef(true);
  const loanId = loan?.loanId;
  const encompassLoanId = loan?.encompassLoanId;
  const identity = useMemo(
    () => (loanId && encompassLoanId ? { loanId, encompassLoanId } : null),
    [loanId, encompassLoanId]
  );
  const listUnresolved = useCallback(
    () => (identity ? listUnresolvedPaymentLinks(identity) : Promise.resolve([])),
    [identity]
  );

  useEffect(() => {
    live.current = true;
    const counter = request;
    return () => {
      live.current = false;
      counter.current++;
    };
  }, []);

  // The newest search wins: every keystroke advances the counter, including the ones too short to search.
  useEffect(() => {
    const term = query.trim();
    const current = ++request.current;
    if (term.length < 2) {
      setFound(null);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    setSearchError(null);
    const timer = window.setTimeout(() => {
      searchPaymentLinkLoans(term)
        .then((result) => {
          if (request.current === current) {
            setFound({ term, result });
          }
        })
        .catch((cause: unknown) => {
          if (request.current === current) {
            setFound(null);
            setSearchError(cause instanceof Error ? cause.message : 'Loans could not be searched.');
          }
        })
        .finally(() => {
          if (request.current === current) {
            setSearching(false);
          }
        });
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [query, retry]);

  // The search screen starts in its field, after the dialog has placed its own focus.
  useEffect(() => {
    if (loan) {
      return;
    }
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [loan]);

  const pick = (row: AppraisalPaymentLinkLoan) => {
    setError(null);
    setLoan(row);
  };
  const changeLoan = () => {
    setError(null);
    setLoan(null);
    setRetry((count) => count + 1);
  };
  const save = async (input: AddPaymentLinkInput) => {
    if (!loan || !identity || saving.current) {
      return;
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      const { requestId } = await addPaymentLink(identity, input);
      toast.success('Payment link saved.');
      if (live.current) {
        onSaved(requestId);
      } else {
        // Closed while saving: the report shows the link, and a dialog opened since stays as it is.
        void onReload();
      }
    } catch (cause) {
      if (!live.current) {
        return;
      }
      setError(cause instanceof Error ? cause.message : 'The payment link could not be saved.');
      // Show what the server kept, and ask about Replace again if the loan's current link changed.
      const [, refreshed] = await Promise.allSettled([onReload(), searchPaymentLinkLoans(loan.loanNumber)]);
      const row = refreshed.status === 'fulfilled' ? refreshed.value.rows.find((candidate) => candidate.loanId === loan.loanId) : null;
      if (live.current && row) {
        setLoan(row);
      }
    } finally {
      saving.current = false;
      if (live.current) {
        setBusy(false);
      }
    }
  };

  const rows = found?.result.rows ?? [];
  const status = query.trim().length < 2
    ? 'Search Active LW loans you can open.'
    : searching
      ? 'Searching…'
      : searchError
        ? ''
        : !found
          ? ''
          : rows.length === 0
            ? `No Active LW loan you can open matches “${found.term}”. Funded and closed loans aren’t listed.`
            : `${rows.length} ${rows.length === 1 ? 'loan' : 'loans'}${found.result.more ? ' shown. Keep typing to narrow it down.' : ''}`;

  return (
    <LwModal
      className="appraisal-modal payment-loan-modal"
      eyebrow="APPRAISAL PAYMENT"
      title="Add payment link"
      sub={loan ? 'The borrower gets the link only when you send a reminder.' : 'Find the loan this link is for.'}
      onClose={onClose}
      footer={
        loan ? (
          <PaymentLinkFooter formId={formId} busy={busy} onCancel={onClose} />
        ) : (
          <LwButton variant="sm" onClick={onClose}>
            Cancel
          </LwButton>
        )
      }
    >
      {loan ? (
        <>
          <div className="payment-loan-chosen">
            <div>
              <p>
                <strong>{loan.loanNumber}</strong>
                {loan.borrowerName && <span>{loan.borrowerName}</span>}
              </p>
              <p>{propertyLine(loan)}</p>
            </div>
            <LwButton variant="sm" disabled={busy} onClick={changeLoan}>
              Change loan
            </LwButton>
          </div>
          <PaymentLinkForm
            formId={formId}
            current={loan.currentLink ?? undefined}
            isAdmin={isAdmin}
            formatDate={appraisalDate}
            busy={busy}
            error={error}
            autoFocus
            onListUnresolved={listUnresolved}
            onSubmit={(input) => void save(input)}
          />
        </>
      ) : (
        <div className="payment-loan-search">
          <div
            onKeyDown={(event) => {
              // A complete loan number usually finds one loan: Enter takes it.
              if (event.key === 'Enter' && !searching && rows.length === 1) {
                event.preventDefault();
                pick(rows[0]);
              }
            }}
          >
            <LwSearchInput
              ref={searchRef}
              value={query}
              onChange={setQuery}
              ariaLabel="Loan number, borrower, or address"
              placeholder="Loan number, borrower, or address"
            />
          </div>
          <p role="status" className="payment-loan-search__status">
            {status}
          </p>
          {searchError && (
            <div role="alert" className="payment-loan-search__error">
              <p>{searchError}</p>
              <LwButton variant="sm" onClick={() => setRetry((count) => count + 1)}>
                Try again
              </LwButton>
            </div>
          )}
          {rows.length > 0 && (
            <ul className="payment-loan-results" aria-label="Matching loans">
              {rows.map((row) => (
                <li key={row.loanId}>
                  <button type="button" onClick={() => pick(row)}>
                    <span className="payment-loan-results__lead">
                      <strong>{row.loanNumber}</strong>
                      {row.borrowerName && <span>{row.borrowerName}</span>}
                    </span>
                    <span className="payment-loan-results__address">{propertyLine(row)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </LwModal>
  );
}
